import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import Plot from './Plot';
import { Note } from '../types';
import { buildPlotlyNoteOverlay, noteIdFromPlotlyClick } from '../utils/plotlyNoteOverlay';
import { toPlotlyLocal, axisTimeLabel } from '../utils/plotlyLocalTime';
import { unanalysedDaysOverlay } from '../utils/unanalysedDays';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { RAW_CHART_TOP_MARGIN, rawChartTitle } from '../utils/plotlyTitle';
import { inH2OPlotlyTicks } from '../utils/pressureAxis';
import Button from './ui/Button';
import { Checkbox } from './pilot-ui/checkbox';

interface ScatterPlotReading {
  time: string;
  pressure: number;
}

interface ScatterPlotData {
  device_name: string;
  start_date: string;
  end_date: string;
  // FS-622: IANA zone of the device's own location, resolved server-side from
  // the ZIP on file. Absent when the device has no ZIP or no cached
  // coordinates for it — then the axis says it is showing the viewer's time.
  time_zone?: string | null;
  readings: ScatterPlotReading[];
}

interface ScatterPlotChartProps {
  deviceId: string;
  startDate: string;
  endDate: string;
  notes?: Note[];
  noteColor?: string;
  onNoteImageClick?: (noteId: number) => void;
  /** FS-723: the last day the daily analysis has processed (YYYY-MM-DD). When
   *  `endDate` runs past it, the later days are raw readings only — the
   *  caption names both ends and a band shades the unanalysed days. */
  analysedThrough?: string;
}

// Browser-remembered preference: anchor the pressure axis at zero instead of
// letting Plotly fit the data range.
const ZERO_PREF_KEY = 'scatterPlotChart.includeZero';

const ScatterPlotChart: React.FC<ScatterPlotChartProps> = ({
  deviceId,
  startDate,
  endDate,
  notes,
  noteColor = '#2563eb',
  onNoteImageClick,
  analysedThrough,
}) => {
  const [includeZero, setIncludeZero] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ZERO_PREF_KEY) === '1';
    } catch {
      return false;
    }
  });
  // The Pa range the operator has zoomed or panned to, or null while Plotly
  // fits the data — the inH₂O ticks are laid out over whichever is showing.
  const [yView, setYView] = useState<[number, number] | null>(null);
  const toggleIncludeZero = () => {
    const next = !includeZero;
    setIncludeZero(next);
    // The y uirevision below re-fits the axis on a flip; follow it.
    setYView(null);
    try {
      localStorage.setItem(ZERO_PREF_KEY, next ? '1' : '0');
    } catch {
      // storage unavailable (private mode) — preference just won't persist
    }
  };

  const [data, setData] = useState<ScatterPlotData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);

  const fetchScatterPlotData = async () => {
    setLoading(true);
    setError(null);

    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff > 400) {
      setError('Date range too large (max 400 days). Please reduce the range.');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.get<ScatterPlotData>(config.scatterPlotApiUrl!, {
        params: {
          device_id: deviceId,
          start_date: startDate,
          end_date: endDate
        },
        timeout: 60000
      });

      setData(response.data);
      setIsVisible(true);
    } catch (err) {
      const axiosError = err as { code?: string; response?: { status?: number; data?: { error?: string } } };
      if (axiosError.code === 'ECONNABORTED') {
        setError('Request timed out. Try a smaller date range.');
      } else if (axiosError.response?.status === 503) {
        setError('Service timeout. The date range may be too large - try reducing it.');
      } else {
        setError('Failed to load scatter plot data. ' + (axiosError.response?.data?.error || ''));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    // Showing the chart mounts a fresh plot fitted to the data, and hiding it
    // throws the zoom away; either way the inH₂O ticks start from the fit.
    setYView(null);
    if (isVisible) {
      setIsVisible(false);
    } else if (!data) {
      fetchScatterPlotData();
    } else {
      setIsVisible(true);
    }
  };

  useEffect(() => {
    if (isVisible && chartRef.current) {
      setTimeout(() => {
        chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isVisible]);

  const deviceTimeZone = data?.time_zone || null;

  const noteOverlay = buildPlotlyNoteOverlay({
    notes,
    color: noteColor,
    rangeStart: startDate,
    rangeEnd: endDate,
    timeZone: deviceTimeZone,
  });

  // Prepare plotly data. The API returns null (not []) when the range has
  // no readings, so guard before mapping.
  const readings = data?.readings ?? [];
  const timeAxisLabel = axisTimeLabel(deviceTimeZone, readings.map((r) => r.time));
  const basePlotData = data ? [{
    x: readings.map(r => toPlotlyLocal(r.time, deviceTimeZone)),
    y: readings.map(r => r.pressure),
    mode: 'markers' as const,
    type: 'scatter' as const,
    marker: {
      size: 4,
      color: 'rgba(59, 130, 246, 0.6)',
      line: {
        color: 'rgba(59, 130, 246, 1)',
        width: 0.5
      }
    },
    name: 'Pressure'
  }] : [];
  const csvColumns: CsvColumn<(typeof readings)[number]>[] = [
    { header: 'Time (UTC)', value: 'time' },
    { header: timeAxisLabel, value: (r) => toPlotlyLocal(r.time, deviceTimeZone) },
    { header: 'Pressure (Pa)', value: 'pressure' },
  ];
  // Plotly draws only the axes a trace is bound to, so one invisible point
  // binds the inH₂O axis (FS-709). It is a real reading, inside the data, so it
  // never stretches the shared range; it takes no hover and no click.
  const inH2OAnchor = readings.length > 0 ? [{
    x: [toPlotlyLocal(readings[0].time, deviceTimeZone)],
    y: [readings[0].pressure],
    yaxis: 'y3',
    type: 'scatter' as const,
    mode: 'markers' as const,
    marker: { opacity: 0 },
    hoverinfo: 'skip' as const,
    showlegend: false,
  }] : [];
  const plotData = [
    ...basePlotData,
    ...inH2OAnchor,
    ...(noteOverlay.trace ? [noteOverlay.trace] : []),
  ];

  // FS-723: days after the last processed one have readings but no daily
  // analysis yet — shaded, labelled and captioned (shared with the
  // temperature plot).
  const unanalysed = unanalysedDaysOverlay(endDate, analysedThrough, deviceTimeZone);

  // FS-709: inches of water on the right. Over the fitted view the ticks span
  // the data (and zero when asked) plus a margin for Plotly's own padding;
  // ticks past the visible range are simply not drawn.
  let yLo = Infinity;
  let yHi = -Infinity;
  readings.forEach((r) => {
    if (r.pressure < yLo) yLo = r.pressure;
    if (r.pressure > yHi) yHi = r.pressure;
  });
  if (includeZero) {
    yLo = Math.min(0, yLo);
    yHi = Math.max(0, yHi);
  }
  const yPad = Math.max((yHi - yLo) * 0.1, 1);
  const inH2OTicks = yView
    ? inH2OPlotlyTicks(Math.min(...yView), Math.max(...yView))
    : inH2OPlotlyTicks(yLo - yPad, yHi + yPad);

  // Zoom, pan and an axis drag report the new y range; autoscale and a
  // double-click report autorange. Either axis may be the one named, since the
  // two are matched.
  const handleRelayout = (event: Record<string, unknown>) => {
    for (const ax of ['yaxis', 'yaxis3']) {
      if (event[`${ax}.autorange`]) {
        setYView(null);
        return;
      }
      const whole = event[`${ax}.range`];
      const r0 = Array.isArray(whole) ? whole[0] : event[`${ax}.range[0]`];
      const r1 = Array.isArray(whole) ? whole[1] : event[`${ax}.range[1]`];
      if (typeof r0 === 'number' && typeof r1 === 'number') {
        setYView([r0, r1]);
        return;
      }
    }
  };

  const layout = {
    title: rawChartTitle(data ? `Scatter Plot - ${data.device_name}` : 'Scatter Plot'),
    xaxis: {
      title: { text: timeAxisLabel },
      type: 'date' as const
    },
    yaxis: {
      title: { text: 'Pressure (Pa)' },
      // 'tozero' extends the autorange to include 0 without clipping the
      // slightly-negative readings ('nonnegative' is the mode that clips).
      rangemode: includeZero ? ('tozero' as const) : ('normal' as const),
      // Re-autorange this axis when the preference flips — the layout-level
      // uirevision below would otherwise preserve a user zoom and swallow the
      // toggle. X-axis zoom/pan survives either way.
      uirevision: includeZero ? 'zero' : 'fit',
    },
    // The inH₂O axis: same range as the Pa axis through every interaction
    // (`matches`), labelled at round inch values.
    yaxis3: {
      overlaying: 'y' as const,
      matches: 'y' as const,
      side: 'right' as const,
      title: { text: 'Pressure (inH₂O)' },
      tickmode: 'array' as const,
      tickvals: inH2OTicks.tickvals,
      ticktext: inH2OTicks.ticktext,
      showgrid: false,
      zeroline: false,
      automargin: true,
      rangemode: includeZero ? ('tozero' as const) : ('normal' as const),
      uirevision: includeZero ? 'zero' : 'fit',
    },
    // Overlay y-axis used only by note markers (pinned to top of plot).
    yaxis2: {
      overlaying: 'y' as const,
      showgrid: false,
      showticklabels: false,
      zeroline: false,
      range: [0, 1],
      fixedrange: true,
    },
    shapes: [...unanalysed.shapes, ...noteOverlay.shapes],
    annotations: unanalysed.annotations,
    hovermode: 'closest' as const,
    // One series, named by the axis title. Plotly turns a legend on by itself
    // once a second trace exists, and the inH₂O anchor is one.
    showlegend: false,
    autosize: true,
    margin: { t: RAW_CHART_TOP_MARGIN, r: 70, b: 50, l: 60 },
    // Preserve user zoom/pan across re-renders (e.g. the AuthContext 60s
    // token check re-renders the tree with a fresh layout object).
    uirevision: 'keep'
  };

  const handleClick = (event: unknown) => {
    const noteId = noteIdFromPlotlyClick(event);
    if (noteId != null && onNoteImageClick) onNoteImageClick(noteId);
  };

  return (
    <div className="mt-4">
      <Button
        variant="link"
        size="md"
        onClick={handleToggle}
        disabled={loading}
        title="Show or hide a plot of every raw pressure reading in this date range (they load the first time you open it)"
      >
        {loading ? 'Loading...' : isVisible ? 'Hide Scatter Chart' : 'View Raw Scatter Chart'}
      </Button>

      {error && (
        <div className="mt-2 text-status-error-foreground text-sm">
          {error}
        </div>
      )}

      {isVisible && data && readings.length === 0 && (
        <div className="mt-2 text-sm text-muted-foreground">
          No readings for this device in the selected date range.
        </div>
      )}

      {isVisible && data && readings.length > 0 && (
        <div ref={chartRef} className="mt-4 border rounded-lg p-4 bg-white">
          <div className="flex justify-end">
            <ChartCsvButton
              chart="raw-pressure"
              rows={readings}
              columns={csvColumns}
              startDate={data.start_date}
              endDate={data.end_date}
            />
          </div>
          <Plot
            data={plotData}
            layout={layout}
            config={{ responsive: true }}
            style={{ width: '100%', height: '500px' }}
            onClick={handleClick}
            onRelayout={handleRelayout}
          />
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 select-none cursor-pointer w-fit">
            <Checkbox
              className="size-6"
              checked={includeZero}
              onCheckedChange={toggleIncludeZero}
              title="Include zero on the pressure axis"
            />
            Include zero on the pressure axis
          </label>
          <div className="mt-2 text-xs text-muted-foreground">
            Date Range: {data.start_date} to {data.end_date} | Total Readings: {readings.length}
          </div>
          {unanalysed.caption && (
            <div className="mt-1 text-xs text-muted-foreground">{unanalysed.caption}</div>
          )}
          <div className="mt-1 text-xs text-muted-foreground">
            Readings are adjusted by the device&apos;s static calibration offset only; the
            daily dynamic calibration used by the analysis charts is not applied here.
          </div>
        </div>
      )}
    </div>
  );
};

export default ScatterPlotChart;
